/**
 * @file DotPlotView component
 * @description Pure presentation component for rendering a dot plot visualization.
 * Displays roster values, team average, and current player value on a scaled chart.
 * @remarks Label widths are set to 150px (centered with -75px margin) to prevent
 * truncation of longer player names in the dot plot chart.
 */

import { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/* design-system: dynamic-style waiver — the styles below are measured dot-plot
   positions (computed % offsets) and fixed label geometry, hoisted out of JSX. */

/**
 * Horizontal dot position.
 * @param leftPct - Position as a percentage of the track
 */
const dotLeftStyle = (leftPct: number) => ({ left: `${leftPct}%` });

/**
 * Floating label position.
 * @param leftPct - Horizontal position as a percentage
 * @param top - Vertical offset in pixels
 */
const labelPosStyle = (leftPct: number, top: number) => ({
  left: `${leftPct}%`,
  top,
});

/** Player/team-average label text geometry (150px, centered). */
const LABEL_TEXT_STYLE = {
  marginLeft: -75,
  width: 150,
};

/**
 * Props for DotPlotView component
 */
interface DotPlotViewProps {
  /** Current player's metric value */
  currentValue: number;
  /** Array of roster players' metric values for comparison */
  rosterValues: number[];
  /** Player's last name to display on the chart */
  lastName: string;
  /** Whether dark mode styling should be applied */
  isDark: boolean;
  /** Whether small screen styling should be applied */
  isSmallScreen: boolean;
  /** Calculated team average value */
  teamAverage: number;
  /** Highest value in the dataset */
  highestValue: number;
  /** Maximum scale value for the chart */
  maxScale: number;
  /** Array of scale label values to display */
  dotPlotLabelArray: number[];
  /** Function to calculate position percentage from value */
  getPosition: (value: number) => number;
}

/**
 * DotPlot view component
 * @param props - The component props
 * @param props.currentValue - Current player's metric value
 * @param props.rosterValues - Array of roster players' metric values for comparison
 * @param props.lastName - Player's last name to display on the chart
 * @param props.isDark - Whether dark mode styling should be applied
 * @param props.isSmallScreen - Whether small screen styling should be applied
 * @param props.teamAverage - Calculated team average value
 * @param props.highestValue - Highest value in the dataset
 * @param props.maxScale - Maximum scale value for the chart
 * @param props.dotPlotLabelArray - Array of scale label values to display
 * @param props.getPosition - Function to calculate position percentage from value
 * @remarks
 * Pure UI component that receives all data and callbacks as props.
 * Wrapped in React.memo at export for performance optimization.
 */
const DotPlotView = ({
  currentValue,
  rosterValues,
  lastName,
  isDark,
  isSmallScreen,
  teamAverage,
  dotPlotLabelArray,
  getPosition,
}: DotPlotViewProps) => (
  <Stack
    className={`gap-1 rounded-none bg-surface-base px-8 py-10 ${isSmallScreen ? "border-2 border-background-gray" : "border border-surface-muted"}`}
  >
    <Box className="h-20 w-full">
      {/* Scale line */}
      <Box
        className={`absolute mt-12 h-px w-full rounded-full ${isDark ? "bg-content-muted" : "bg-content-subtle"}`}
      />

      {/* Roster dots */}
      {rosterValues.map((value, index) => (
        <Box
          key={`roster-${index}`}
          className="absolute -ml-2 mt-8 size-4 rounded-full border-2 border-chart-other bg-chart-other/40 -translate-y-2"
          // eslint-disable-next-line no-restricted-syntax -- computed % dot position (left `${pct}%`); data-driven, no closed token for percentage offset
          UNSAFE_style={dotLeftStyle(getPosition(value))}
        />
      ))}

      {/* Team average indicator */}
      <Box
        className="absolute -ml-2 mt-8 size-4 rounded-full border-2 border-chart-horizontalLine bg-chart-horizontalLine -translate-y-2"
        // eslint-disable-next-line no-restricted-syntax -- computed % dot position (left `${pct}%`); data-driven, no closed token
        UNSAFE_style={dotLeftStyle(getPosition(teamAverage))}
      />

      {/* Current player dot */}
      <Box
        className="absolute -ml-2 mt-8 size-4 rounded-full border-2 border-chart-player bg-chart-player -translate-y-2"
        // eslint-disable-next-line no-restricted-syntax -- computed % dot position (left `${pct}%`); data-driven, no closed token
        UNSAFE_style={dotLeftStyle(getPosition(currentValue))}
      />

      {/* Scale labels */}
      {dotPlotLabelArray.map((scaleLabel, index) => (
        <Box
          key={`scale-label-${index}`}
          className="absolute mt-12"
          // eslint-disable-next-line no-restricted-syntax -- computed % track position (evenly-spaced label offset); data-driven, no closed token
          UNSAFE_style={dotLeftStyle(
            (index / (dotPlotLabelArray?.length - 1)) * 100
          )}
        >
          <Text
            variant="body"
            className={`-ml-5 w-10 text-center ${isDark ? "text-content-subtle" : "text-needs-semantic-background-600"}`}
          >
            {scaleLabel}
          </Text>
        </Box>
      ))}

      {/* Current player label */}
      <Box
        className="absolute -top-4"
        // eslint-disable-next-line no-restricted-syntax -- computed % label position (left `${pct}%`); data-driven, no closed token
        UNSAFE_style={dotLeftStyle(getPosition(currentValue))}
      >
        <Text
          variant="body-strong"
          className="text-center text-chart-player"
          numberOfLines={1}
          // eslint-disable-next-line no-restricted-syntax -- off-scale label geometry (width 150 / marginLeft -75) to prevent name truncation; no closed token
          UNSAFE_style={LABEL_TEXT_STYLE}
        >
          {lastName}
        </Text>
      </Box>

      {/* Team average label */}
      <Box
        className="absolute"
        // eslint-disable-next-line no-restricted-syntax -- computed % label position with fixed top 80px; data-driven left, no closed token
        UNSAFE_style={labelPosStyle(getPosition(teamAverage), 80)}
      >
        <Text
          variant="body-strong"
          className="text-center text-chart-horizontalLine"
          // eslint-disable-next-line no-restricted-syntax -- off-scale label geometry (width 150 / marginLeft -75) to prevent name truncation; no closed token
          UNSAFE_style={LABEL_TEXT_STYLE}
        >
          Team Average
        </Text>
      </Box>
    </Box>
  </Stack>
);

DotPlotView.displayName = "DotPlotView";

export default memo(DotPlotView);
